import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';

import { store } from './store/store';
import { AuthProvider } from './contexts/AuthContext';
import { FeaturesProvider } from './contexts/FeaturesContext';
import FeatureGate from './components/FeatureGate';
import { useFeatures } from './contexts/FeaturesContext';
import RealtorNotAvailablePage from './pages/RealtorNotAvailablePage';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardRouter from './components/dashboards/DashboardRouter';
import ProjectsRouter from './components/ProjectsRouter';
import PaymentsRouter from './components/PaymentsRouter';
import ContractorsRouter from './components/ContractorsRouter';
import EnhancedContractorProfile from './components/contractors/EnhancedContractorProfile';
import ProfilePage from './pages/ProfilePage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import MyQuoteRequestsPage from './pages/MyQuoteRequestsPage';
import ReceivedQuoteRequestsPage from './pages/ReceivedQuoteRequestsPage';
import MarketplacePage from './pages/MarketplacePage';
import AdminDashboard from './pages/AdminDashboard';
import AdminHomePage from './pages/AdminHomePage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminUserDetailPage from './pages/AdminUserDetailPage';
import AdminApplicantsPage from './pages/AdminApplicantsPage';
import AdminApplicationDetailPage from './pages/AdminApplicationDetailPage';
import AdminClientProjectsPage from './pages/AdminClientProjectsPage';
import AdminProjectDetailPage from './pages/AdminProjectDetailPage';
import AdminVerificationsPage from './pages/AdminVerificationsPage';
import AdminContractorVerificationDetailPage from './pages/AdminContractorVerificationDetailPage';
import AdminListingsPage from './pages/AdminListingsPage';
import AdminListingReviewPage from './pages/AdminListingReviewPage';
import AdminVerifierWorkPage from './pages/AdminVerifierWorkPage';
import AdminContractorVerificationsPage from './pages/AdminContractorVerificationsPage';
import AdminSignupSettingsPage from './pages/AdminSignupSettingsPage';
import AdminMerchantCatalogsPage from './pages/AdminMerchantCatalogsPage';
import AdminWhatsAppBriefsPage from './pages/AdminWhatsAppBriefsPage';
import AdminVerifierSettingsPage from './pages/AdminVerifierSettingsPage';
import AdminVerifierPayoutsPage from './pages/AdminVerifierPayoutsPage';
import AdminReviewsPage from './pages/AdminReviewsPage';
import AdminVerificationPage from './pages/AdminVerificationPage';
import AdminApplicationsPage from './pages/AdminApplicationsPage';
import AdminConversationsPage from './pages/AdminConversationsPage';
import AdminSecurityPage from './pages/AdminSecurityPage';
import VerifierDashboard from './pages/VerifierDashboard_Enhanced';
import VerifierAssignmentsPage from './pages/VerifierAssignmentsPage';
import VerifierContractorVerificationPage from './pages/VerifierContractorVerificationPage';
import VerifierVettingOfferPage from './pages/VerifierVettingOfferPage';
import VerifierProfile_Enhanced from './pages/VerifierProfile_Enhanced';
import VerifierSettingsPage from './pages/VerifierSettingsPage';
import UnifiedOnboardingModal from './components/onboarding/UnifiedOnboardingModal';
import EqayaAIChatWidget from './components/EqayaAIChatWidget';
import VerifierPortfolioPage from './pages/VerifierPortfolioPage';
import ContractorPortfolioPage from './pages/ContractorPortfolioPage';
import VerifiersPage from './pages/VerifiersPage';
import VerifierPublicProfile from './pages/VerifierPublicProfile';
import PropertiesPage from './pages/PropertiesPage';
import FindRealtorPage from './pages/FindRealtorPage';
import FindConveyancerPage from './pages/FindConveyancerPage';
import BuyingPropertyPage from './pages/BuyingPropertyPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import ProjectManagersPage from './pages/ProjectManagersPage';
import MerchantProfilePage from './pages/MerchantProfilePage';
import QuoteComparisonPage from './pages/QuoteComparisonPage';
import BidComparisonPage from './pages/BidComparisonPage';
import BOQTrackerPage from './pages/BOQTrackerPage';
import CheckoutPage from './pages/CheckoutPage';
import MerchantQuoteRequestPage from './pages/MerchantQuoteRequestPage';
import MerchantQuoteRequestsPage from './pages/MerchantQuoteRequestsPage';
import MerchantOrdersPage from './pages/MerchantOrdersPage';
import MerchantProductsPage from './pages/MerchantProductsPage';
import MerchantPaymentsPage from './pages/MerchantPaymentsPage';
import MerchantDocumentsPage from './pages/MerchantDocumentsPage';
import MerchantReviewsPage from './pages/MerchantReviewsPage';
import MerchantsPage from './pages/MerchantsPage';
import RealtorProfilePage from './pages/RealtorProfilePage';
import RealtorDocumentsPage from './pages/RealtorDocumentsPage';
import ConveyancerProfilePage from './pages/ConveyancerProfilePage';
import ConveyancerDocumentsPage from './pages/ConveyancerDocumentsPage';
import RealtorListingsPage from './pages/RealtorListingsPage';
import RealtorListingEditorPage from './pages/RealtorListingEditorPage';
import ListingVerificationPaymentSuccess from './pages/ListingVerificationPaymentSuccess';
import OrderDetailPage from './pages/OrderDetailPage';
import ClientQuoteRequestsPage from './pages/ClientQuoteRequestsPage';
import OffersPage from './pages/OffersPage';
import ClientDeliveryConfirmation from './components/delivery/ClientDeliveryConfirmation';
import MerchantDeliverySubmission from './components/delivery/MerchantDeliverySubmission';
import CreateVerificationRequest from './pages/CreateVerificationRequest';
import ServicesHub from './pages/ServicesHub';
import BuildAProjectLanding from './pages/BuildAProjectLanding';
import PropertyCheckLanding from './pages/PropertyCheckLanding';
import BookPropertyCheck from './pages/BookPropertyCheck';
import PropertyCheckDashboard from './pages/PropertyCheckDashboard';
import PropertyCheckDetail from './pages/PropertyCheckDetail';
import PropertyCheckPaymentSuccess from './pages/PropertyCheckPaymentSuccess';
import OwnerProperties from './pages/OwnerProperties';
import AdminPropertyCheckPackages from './pages/AdminPropertyCheckPackages';
import ClientVerificationRequestsPage from './pages/ClientVerificationRequestsPage';
import VerificationProposalsPage from './pages/VerificationProposalsPage';
// VerifierProfilePage removed - now using role-based ProfilePage router
import InspectorInspectionsPage from './pages/InspectorInspectionsPage';
import InspectorEarningsPage from './pages/InspectorEarningsPage';
import InspectorReportsPage from './pages/InspectorReportsPage';
import AIGuidedVerificationPage from './pages/AIGuidedVerificationPage';
import VerifierHelpPage from './pages/VerifierHelpPage';
import StructuredVerificationWorkflow from './pages/StructuredVerificationWorkflow';
import SimpleVerificationReportPage from './pages/SimpleVerificationReportPage';
import AvailableSimpleVerificationsPage from './pages/AvailableSimpleVerificationsPage';
import MySimpleVerificationsPage from './pages/MySimpleVerificationsPage';
import OfflineSyncStatusPage from './pages/OfflineSyncStatusPage';
import ContractorMarketplacePage from './pages/ContractorMarketplacePage';
import ContractorFinancialHub from './pages/ContractorFinancialHub';
import ContractorDocumentsPage from './pages/ContractorDocumentsPage';
import WorkHistoryPage from './pages/WorkHistoryPage';
import MessagingCenter from './components/communications/MessagingCenter';
import ProposalManagement from './components/contractor/ProposalManagement';
import MessagingPage from './pages/MessagingPage';
import ContractorBidsPage from './components/bids/ContractorBidsPage';
import ClientBidReviewPage from './components/bids/ClientBidReviewPage';
import BidDetailView from './components/bids/BidDetailView';
import MilestoneReportReviewPage from './pages/MilestoneReportReviewPage';
import MaterialsMarketplace from './pages/marketplace/MaterialsMarketplace';
import EquipmentMarketplace from './pages/marketplace/EquipmentMarketplace';
import ProfessionalsMarketplace from './pages/marketplace/ProfessionalsMarketplace';
import InsuranceMarketplace from './pages/marketplace/InsuranceMarketplace';
import InspectorsMarketplace from './pages/marketplace/InspectorsMarketplace';
import DisputesPage from './pages/DisputesPage';
import InvoicesPage from './pages/InvoicesPage';
import ReviewsPage from './pages/ReviewsPage';
import AdminAppealsPage from './pages/AdminAppealsPage';
import ContractorWelcomePage from './pages/ContractorWelcomePage';
import ContractorApplicationPage from './pages/ContractorApplicationPage';
import ContractorApplicationStatusPage from './pages/ContractorApplicationStatusPage';
import VerifierApplicationPage from './pages/VerifierApplicationPage';
import VerifierApplicationStatusPage from './pages/VerifierApplicationStatusPage';
import VerifierWelcomePage from './pages/VerifierWelcomePage';
import WhatIsEqayaPage from './pages/WhatIsEqayaPage';
import TrustPage from './pages/TrustPage';
import StartProjectPage from './pages/StartProjectPage';
import PublicInfoPage from './pages/PublicInfoPage';
import MagicLoginPage from './pages/MagicLoginPage';
import ForContractorsPage from './pages/ForContractorsPage';
import ForVerifiersPage from './pages/ForVerifiersPage';
import VerifierAgreementPage from './pages/VerifierAgreementPage';
import NotFoundPage from './pages/NotFoundPage';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import LogoutHandler from './components/LogoutHandler';
import EmailVerificationFlow from './components/EmailVerification/EmailVerificationFlow';
import NotificationPreferences from './components/Notifications/NotificationPreferences';
import AnalyticsDashboard from './components/Analytics/AnalyticsDashboard';
import SupabaseTestComponent from './components/examples/SupabaseTestComponent';
import NotificationListener from './components/NotificationListener';
import { loadGoogleMaps } from './utils/loadGoogleMaps';
import { initAnalytics, trackPageView } from './utils/analytics';
import { brand, status, text, bg, border, shadow, easing } from './theme/colors';
import './App.css';

// The old agency-only lookup is now part of Find a realtor (name search + register).
const VerifyAgentRedirect = () => {
  const q = new URLSearchParams(window.location.search).get('q');
  return <Navigate to={`/realtors${q ? `?q=${encodeURIComponent(q)}` : ''}`} replace />;
};

/**
 * /admin/home/<group>/<state> was the old shape; approval is now a ?approval=
 * filter. Translate rather than 404 so existing links and bookmarks survive.
 */
const ApprovalPathRedirect = ({ base }) => {
  const { approvalStatus } = useParams();
  const valid = ['approved', 'pending', 'rejected'].includes(approvalStatus);
  return <Navigate to={valid ? `${base}?approval=${approvalStatus}` : base} replace />;
};

// Load Google Maps API dynamically (key from env var, not hardcoded)
loadGoogleMaps().catch(err => console.warn('[App] Google Maps load failed:', err.message));

// Initialize GA4 analytics
initAnalytics();

// Track route changes for GA4
function RouteTracker() {
  const location = useLocation();
  React.useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

// ── Design tokens (imported from theme/colors.js) ────────────────────────

// Color-tinted shadow system: base → elevated → floating
const shadows = (() => {
  const base = Array(25).fill('none');
  base[0] = 'none';
  base[1] = shadow.sm;
  base[2] = '0 2px 6px rgba(13,148,136,0.10), 0 1px 3px rgba(13,148,136,0.07)';
  base[3] = shadow.md;
  base[4] = '0 6px 16px rgba(13,148,136,0.14), 0 3px 6px rgba(13,148,136,0.09)';
  base[6] = shadow.lg;
  base[8] = '0 12px 32px rgba(13,148,136,0.18), 0 6px 12px rgba(13,148,136,0.11)';
  base[12] = shadow.xl;
  base[16] = '0 20px 48px rgba(13,148,136,0.22), 0 10px 20px rgba(13,148,136,0.13)';
  base[24] = '0 24px 56px rgba(13,148,136,0.25), 0 12px 24px rgba(13,148,136,0.14)';
  for (let i = 1; i < 25; i++) { if (base[i] === 'none' && i > 0) base[i] = base[i - 1]; }
  return base;
})();

// Font stacks
const displayFont = '"DM Serif Display", Georgia, "Times New Roman", serif';
const bodyFont = '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const monoFont = '"JetBrains Mono", "Fira Code", "Cascadia Code", Consolas, monospace';

const theme = createTheme({
  palette: {
    primary: {
      main: brand.primary,
      light: brand.primaryLight,
      dark: brand.primaryDark,
    },
    secondary: {
      main: brand.secondary,
      light: brand.secondaryLight,
      dark: brand.secondaryDark,
    },
    success: { main: status.success, dark: status.successDark },
    warning: { main: status.warning, dark: status.warningDark },
    error:   { main: status.error, dark: status.errorDark },
    info:    { main: status.info, dark: status.infoDark },
    text: {
      primary:   text.primary,
      secondary: text.secondary,
      disabled:  text.disabled,
    },
    background: {
      default: bg.page,
      paper:   bg.paper,
    },
    divider: border.light,
  },
  shadows,
  typography: {
    fontFamily: bodyFont,
    // Display headings: serif font, tight tracking
    h1: { fontFamily: displayFont, letterSpacing: '-0.03em', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontFamily: displayFont, letterSpacing: '-0.03em', fontWeight: 700, lineHeight: 1.25 },
    h3: { fontFamily: displayFont, letterSpacing: '-0.02em', fontWeight: 700, lineHeight: 1.3 },
    h4: { fontFamily: displayFont, letterSpacing: '-0.02em', fontWeight: 600, lineHeight: 1.35 },
    // UI headings: sans-serif, semi-bold
    h5: { fontFamily: bodyFont, letterSpacing: '-0.01em', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontFamily: bodyFont, letterSpacing: '-0.01em', fontWeight: 600, lineHeight: 1.4 },
    // Body text: generous line-height
    body1: { fontFamily: bodyFont, lineHeight: 1.7, fontSize: '1rem' },
    body2: { fontFamily: bodyFont, lineHeight: 1.6, fontSize: '0.875rem' },
    // Mono for data
    overline: { fontFamily: monoFont, letterSpacing: '0.08em', fontSize: '0.75rem', fontWeight: 500 },
    caption: { fontFamily: bodyFont, lineHeight: 1.5, fontSize: '0.75rem', color: text.secondary },
    button: { fontFamily: bodyFont, fontWeight: 600, letterSpacing: '0.01em' },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    // ── Buttons: hover, focus-visible, active on every button ──
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          transition: `background-color 0.2s ${easing.smooth}, transform 0.15s ${easing.spring}, box-shadow 0.2s ${easing.smooth}`,
          '&:hover': {
            boxShadow: '0 4px 12px rgba(13,148,136,0.18)',
          },
          '&:focus-visible': {
            outline: `2px solid ${brand.primary}`,
            outlineOffset: 2,
            boxShadow: `0 0 0 4px rgba(13,148,136,0.2)`,
          },
          '&:active': {
            transform: 'scale(0.97)',
          },
        },
      },
    },
    // ── IconButtons ──
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: `background-color 0.2s ${easing.smooth}, transform 0.15s ${easing.spring}`,
          '&:focus-visible': {
            outline: `2px solid ${brand.primary}`,
            outlineOffset: 2,
            boxShadow: `0 0 0 4px rgba(13,148,136,0.2)`,
          },
          '&:active': {
            transform: 'scale(0.92)',
          },
        },
      },
    },
    // ── Cards: elevated surface with tinted shadow, hover lift ──
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(13,148,136,0.10), 0 1px 3px rgba(13,148,136,0.07)',
          transition: `transform 0.25s ${easing.spring}, box-shadow 0.25s ${easing.smooth}`,
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 8px 24px rgba(13,148,136,0.16), 0 4px 8px rgba(13,148,136,0.10)',
          },
        },
      },
    },
    // ── Paper: base surface ──
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(13,148,136,0.08), 0 1px 2px rgba(13,148,136,0.06)',
        },
        elevation2: {
          boxShadow: '0 2px 8px rgba(13,148,136,0.10), 0 1px 3px rgba(13,148,136,0.07)',
        },
        elevation3: {
          boxShadow: '0 4px 12px rgba(13,148,136,0.12), 0 2px 4px rgba(13,148,136,0.08)',
        },
      },
    },
    // ── App bars / headers: never rounded (a rounded edge reads as "clickable").
    // AppBar extends Paper, which the theme rounds to 10px — square it off. ──
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    // ── Required-field asterisk: always red so mandatory fields are obvious ──
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: '#d32f2f',
        },
      },
    },
    // ── Text fields: focus glow ──
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: `border-color 0.2s ${easing.smooth}, box-shadow 0.2s ${easing.smooth}`,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.primary,
              boxShadow: `0 0 0 3px rgba(13,148,136,0.15)`,
            },
          },
        },
      },
    },
    // ── Chips ──
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          transition: `transform 0.15s ${easing.spring}, box-shadow 0.2s ${easing.smooth}`,
          '&:hover': {
            boxShadow: '0 2px 8px rgba(13,148,136,0.12)',
          },
          '&:active': {
            transform: 'scale(0.96)',
          },
        },
      },
    },
    // ── Links ──
    MuiLink: {
      styleOverrides: {
        root: {
          transition: `color 0.2s ${easing.smooth}, text-decoration-color 0.2s ${easing.smooth}`,
          '&:focus-visible': {
            outline: `2px solid ${brand.primary}`,
            outlineOffset: 2,
            borderRadius: 2,
          },
        },
      },
    },
    // ── Dialogs ──
    MuiDialog: {
      styleOverrides: {
        root: {
          // Disable clicks on the Backdrop overlay so it never fires onClose.
          '& .MuiBackdrop-root': {
            pointerEvents: 'none',
          },
        },
        container: {
          // Also disable container clicks (second detection path).
          pointerEvents: 'none',
        },
        paper: {
          pointerEvents: 'auto',
          position: 'relative', // anchor for absolute-positioned close button
          borderRadius: 14,
          boxShadow: '0 16px 48px rgba(13,148,136,0.20), 0 8px 16px rgba(13,148,136,0.12)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: displayFont,
          fontWeight: 600,
          paddingRight: '48px', // space for close button
        },
      },
    },
    // ── Tabs ──
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          transition: `color 0.2s ${easing.smooth}`,
          '&:focus-visible': {
            outline: `2px solid ${brand.primary}`,
            outlineOffset: -2,
          },
        },
      },
    },
    // ── Tooltips ──
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: bodyFont,
          fontSize: '0.8rem',
          borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

/**
 * The realtor area. Off-switch first: on a deployment without the property
 * listings vertical every realtor path shows the "not available yet" notice.
 */
function RealtorRoutes() {
  const features = useFeatures();
  if (!features.loaded) return null;
  if (!features.listings) {
    return (
      <Routes>
        <Route path="*" element={<RealtorNotAvailablePage />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="profile" element={<RealtorProfilePage />} />
      <Route path="documents" element={<RealtorDocumentsPage />} />
      <Route path="listings" element={<RealtorListingsPage />} />
      <Route path="listings/:id" element={<RealtorListingEditorPage />} />
      <Route path="listings/:id/verification/paid" element={<ListingVerificationPaymentSuccess />} />
      <Route path="" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

/** The conveyancer area — same off-switch as the realtor area. */
function ConveyancerRoutes() {
  const features = useFeatures();
  if (!features.loaded) return null;
  if (!features.listings) {
    return (
      <Routes>
        <Route path="*" element={<RealtorNotAvailablePage tools="Conveyancer tools" />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="profile" element={<ConveyancerProfilePage />} />
      <Route path="documents" element={<ConveyancerDocumentsPage />} />
      <Route path="" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <AuthProvider>
              <FeaturesProvider>
                <CssBaseline />
                <Router>
                  <RouteTracker />
                  <NotificationListener />
                  <Layout>
                    <ErrorBoundary>
                    <Routes>
                      {/* Public routes - redirect authenticated users to dashboard */}
                      <Route 
                        path="/" 
                        element={
                          <PublicRoute>
                            <HomePage />
                          </PublicRoute>
                        } 
                      />
                      
                      {/* Info pages — viewable whether or not you're logged in.
                          PublicRoute would bounce authenticated users back to
                          the dashboard, which made these links appear dead. */}
                      <Route path="/what-is-eqaya" element={<WhatIsEqayaPage />} />
                      <Route path="/trust" element={<TrustPage />} />
                      {/* Describe the project first, create the account last. */}
                      <Route path="/start" element={<StartProjectPage />} />
                      <Route path="/about" element={<PublicInfoPage type="about" />} />
                      <Route path="/contact" element={<PublicInfoPage type="contact" />} />
                      <Route path="/faq" element={<PublicInfoPage type="faq" />} />
                      <Route path="/magic" element={<MagicLoginPage />} />
                      <Route path="/privacy" element={<PublicInfoPage type="privacy" />} />
                      <Route path="/terms" element={<PublicInfoPage type="terms" />} />
                      <Route path="/for-contractors" element={<ForContractorsPage />} />
                      <Route path="/for-verifiers" element={<ForVerifiersPage />} />

                      {/* Authentication routes - redirect to dashboard if already logged in */}
                      <Route 
                        path="/login" 
                        element={
                          <PublicRoute>
                            <LoginPage />
                          </PublicRoute>
                        } 
                      />
                      <Route
                        path="/register"
                        element={
                          <PublicRoute>
                            <RegisterPage />
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/forgot-password"
                        element={
                          <PublicRoute>
                            <ForgotPasswordPage />
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/reset-password"
                        element={
                          <PublicRoute>
                            <ResetPasswordPage />
                          </PublicRoute>
                        }
                      />

                      {/* Onboarding — all roles use UnifiedOnboardingModal */}
                      <Route
                        path="/contractor/onboarding"
                        element={
                          <ProtectedRoute>
                            <UnifiedOnboardingModal userRole="contractor" />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/verifier/onboarding"
                        element={
                          <ProtectedRoute>
                            <UnifiedOnboardingModal userRole="verifier" />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/inspector/onboarding"
                        element={
                          <ProtectedRoute>
                            <UnifiedOnboardingModal userRole="quality_inspector" />
                          </ProtectedRoute>
                        }
                      />
                      {/* Merchant onboarding reuses the merchant profile page (create mode) */}
                      <Route
                        path="/merchant/onboarding"
                        element={
                          <RoleProtectedRoute allowedRoles={['merchant']}>
                            <MerchantProfilePage />
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Verifier portfolio management */}
                      <Route
                        path="/verifier/portfolio"
                        element={
                          <ProtectedRoute>
                            <VerifierPortfolioPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Contractor portfolio builder */}
                      <Route
                        path="/contractor/portfolio"
                        element={
                          <ProtectedRoute>
                            <ContractorPortfolioPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Contractor welcome page - shown after registration */}
                      <Route
                        path="/contractor/welcome"
                        element={
                          <ProtectedRoute>
                            <ContractorWelcomePage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Contractor application routes — reachable while pending (no approval gate) */}
                      <Route
                        path="/contractor/apply"
                        element={
                          <ProtectedRoute>
                            <ContractorApplicationPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/contractor/application-status"
                        element={
                          <ProtectedRoute>
                            <ContractorApplicationStatusPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Verifier welcome page - shown after registration */}
                      <Route
                        path="/verifier/welcome"
                        element={
                          <ProtectedRoute>
                            <VerifierWelcomePage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Verifier application routes — reachable while pending (no approval gate) */}
                      <Route
                        path="/verifier/apply"
                        element={
                          <ProtectedRoute>
                            <VerifierApplicationPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/verifier/application-status"
                        element={
                          <ProtectedRoute>
                            <VerifierApplicationStatusPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Main dashboard route - uses DashboardRouter for role-based routing */}
                      <Route 
                        path="/dashboard" 
                        element={
                          <ProtectedRoute>
                            <DashboardRouter />
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* Legacy dashboard routes - redirect to main dashboard */}
                      <Route path="/contractor-dashboard" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/admin-dashboard" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/verifier-dashboard" element={<Navigate to="/dashboard" replace />} />
                      
                      {/* Protected application routes */}
                      <Route 
                        path="/projects/*" 
                        element={
                          <ProtectedRoute>
                            <ProjectsRouter />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/payments" 
                        element={
                          <ProtectedRoute>
                            <PaymentsRouter />
                          </ProtectedRoute>
                        } 
                      />
                      <Route
                        path="/invoices"
                        element={
                          <ProtectedRoute>
                            <InvoicesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route 
                        path="/disputes" 
                        element={
                          <ProtectedRoute>
                            <DisputesPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/reviews" 
                        element={
                          <ProtectedRoute>
                            <ReviewsPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/profile" 
                        element={
                          <ProtectedRoute>
                            <ProfilePage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/account-settings" 
                        element={
                          <ProtectedRoute>
                            <AccountSettingsPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/my-quote-requests" 
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['client']}>
                              <MyQuoteRequestsPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } 
                      />
                      
                      <Route
                        path="/received-quotes"
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['contractor', 'verifier', 'professional_verifier']}>
                              <ReceivedQuoteRequestsPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        }
                      />

                      {/* Unified client Offers inbox */}
                      <Route
                        path="/offers"
                        element={
                          <RoleProtectedRoute allowedRoles={['client']}>
                            <OffersPage />
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Merchant Quote System Routes */}
                      <Route
                        path="/quotes"
                        element={
                          <RoleProtectedRoute allowedRoles={['client']}>
                            <ClientQuoteRequestsPage />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route 
                        path="/quotes/:quoteRequestId" 
                        element={
                          <ProtectedRoute>
                            <QuoteComparisonPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/checkout" 
                        element={
                          <ProtectedRoute>
                            <CheckoutPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/merchant/quote-request/:quoteRequestId" 
                        element={
                          <RoleProtectedRoute allowedRoles={['merchant']}>
                            <MerchantQuoteRequestPage />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/orders/:orderId" 
                        element={
                          <ProtectedRoute>
                            <OrderDetailPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/orders/:orderId/delivery/confirm" 
                        element={
                          <RoleProtectedRoute allowedRoles={['client']}>
                            <ClientDeliveryConfirmation />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route
                        path="/orders/:orderId/delivery/submit"
                        element={
                          <RoleProtectedRoute allowedRoles={['merchant']}>
                            <MerchantDeliverySubmission />
                          </RoleProtectedRoute>
                        }
                      />
                      
                      {/* Admin-only routes */}
                      <Route 
                        path="/admin/*" 
                        element={
                          <RoleProtectedRoute allowedRoles={['admin']}>
                            <Routes>
                              <Route path="home" element={<AdminHomePage />} />
                              <Route path="home/users" element={<AdminUsersPage />} />
                              <Route path="home/users/:userId" element={<AdminUserDetailPage />} />
                              <Route path="home/users/:userId/verifications" element={<AdminVerifierWorkPage />} />
                              <Route path="home/clients" element={<AdminUsersPage lockedRole="client" title="Clients" noun="client" />} />
                              <Route path="home/contractors" element={<AdminApplicantsPage group="contractors" />} />
                              <Route path="home/verifiers" element={<AdminApplicantsPage group="verifiers" />} />
                              <Route path="home/applications" element={<AdminApplicantsPage group="applications" />} />
                              <Route path="home/applications/:userId" element={<AdminApplicationDetailPage />} />
                              <Route path="home/projects" element={<AdminClientProjectsPage />} />
                              <Route path="home/projects/:projectId" element={<AdminProjectDetailPage />} />
                              <Route path="home/verifications" element={<AdminVerificationsPage />} />
                              <Route path="home/verifications/contractor/:verificationId" element={<AdminContractorVerificationDetailPage />} />
                              <Route path="home/listings" element={<FeatureGate feature="listings"><AdminListingsPage /></FeatureGate>} />
                              <Route path="home/listings/:id" element={<FeatureGate feature="listings"><AdminListingReviewPage /></FeatureGate>} />
                              {/* Approval used to be a path segment; keep those links working. */}
                              <Route path="home/contractors/:approvalStatus" element={<ApprovalPathRedirect base="/admin/home/contractors" />} />
                              <Route path="home/verifiers/:approvalStatus" element={<ApprovalPathRedirect base="/admin/home/verifiers" />} />
                              <Route path="dashboard" element={<AdminDashboard />} />
                              <Route path="appeals" element={<AdminAppealsPage />} />
                              <Route path="reviews" element={<AdminReviewsPage />} />
                              <Route path="verification" element={<AdminVerificationPage />} />
                              <Route path="applications" element={<AdminApplicationsPage />} />
                              <Route path="contractor-verifications" element={<AdminContractorVerificationsPage />} />
                              <Route path="signup-settings" element={<AdminSignupSettingsPage />} />
                              <Route path="merchant-catalogs" element={<AdminMerchantCatalogsPage />} />
                              <Route path="whatsapp-briefs" element={<AdminWhatsAppBriefsPage />} />
                              <Route path="verifier-settings" element={<AdminVerifierSettingsPage />} />
                              <Route path="verifier-payouts" element={<AdminVerifierPayoutsPage />} />
                              <Route path="conversations" element={<AdminConversationsPage />} />
                              <Route path="engagement" element={<AdminDashboard />} />
                              <Route path="revenue" element={<AdminDashboard />} />
                              <Route path="security-console" element={<AdminSecurityPage />} />
                              <Route path="users" element={<AdminDashboard />} />
                              <Route path="settings" element={<AdminDashboard />} />
                              <Route path="property-check-packages" element={<AdminPropertyCheckPackages />} />
                              <Route path="security" element={<AdminDashboard />} />
                              <Route path="analytics" element={<AdminDashboard />} />
                              <Route path="financial" element={<AdminDashboard />} />
                              <Route path="content" element={<AdminDashboard />} />
                              <Route path="withdrawals" element={<AdminDashboard />} />
                              <Route path="refunds" element={<AdminDashboard />} />
                              <Route path="" element={<Navigate to="dashboard" replace />} />
                            </Routes>
                          </RoleProtectedRoute>
                        } 
                      />
                      
                      {/* Verifier-only routes */}
                      <Route 
                        path="/verifier/*" 
                        element={
                          <RoleProtectedRoute allowedRoles={['verifier', 'professional_verifier']}>
                            <Routes>
                              <Route path="my-work" element={<VerifierAssignmentsPage showHistoryTab />} />
                              <Route path="assignments" element={<Navigate to="/verifier/my-work" replace />} />
                              <Route path="contractor-files/:assignmentId" element={<VerifierContractorVerificationPage />} />
                              {/* A vetting job on offer: first to accept gets it. */}
                              <Route path="vetting-offers/:offerId" element={<VerifierVettingOfferPage />} />
                              {/* Quoting is retired — the unified fixed-fee feed replaces the old
                                  marketplace and the separate property-check offers page. */}
                              <Route path="marketplace" element={<Navigate to="/verifier/available-verifications" replace />} />
                              <Route path="property-check-offers" element={<Navigate to="/verifier/available-verifications" replace />} />
                              <Route path="history" element={<Navigate to="/verifier/my-work?tab=completed" replace />} />
                              <Route path="earnings" element={<ContractorFinancialHub role="verifier" />} />
                              <Route path="financial-hub" element={<ContractorFinancialHub role="verifier" />} />
                              <Route path="profile" element={<VerifierProfile_Enhanced />} />
                              <Route path="settings" element={<VerifierSettingsPage />} />
                              <Route path="verify/:id" element={<StructuredVerificationWorkflow />} />
                              <Route path="simple-verifications/:id/report" element={<SimpleVerificationReportPage />} />
                              <Route path="available-verifications" element={<AvailableSimpleVerificationsPage />} />
                              <Route path="my-verifications" element={<MySimpleVerificationsPage />} />
                              <Route path="offline-sync" element={<OfflineSyncStatusPage />} />
                              <Route path="help" element={<VerifierHelpPage />} />
                              <Route path="agreement" element={<VerifierAgreementPage />} />
                              <Route path="" element={<Navigate to="/dashboard" replace />} />
                            </Routes>
                          </RoleProtectedRoute>
                        } 
                      />
                      
                      {/* Quality Inspector-only routes */}
                      <Route 
                        path="/inspector/*" 
                        element={
                          <RoleProtectedRoute allowedRoles={['quality_inspector']}>
                            <Routes>
                              <Route path="dashboard" element={<div>Quality Inspector Dashboard (Available via main dashboard)</div>} />
                              <Route path="inspections" element={<InspectorInspectionsPage />} />
                              <Route path="earnings" element={<InspectorEarningsPage />} />
                              <Route path="reports" element={<InspectorReportsPage />} />
                              <Route path="" element={<Navigate to="dashboard" replace />} />
                            </Routes>
                          </RoleProtectedRoute>
                        } 
                      />
                      
                      {/* Project Manager-only routes */}
                      <Route 
                        path="/pm/*" 
                        element={
                          <RoleProtectedRoute allowedRoles={['project_manager']}>
                            <Routes>
                              <Route path="projects" element={<Navigate to="/projects" replace />} />
                              <Route path="contractors" element={<Navigate to="/contractors" replace />} />
                              <Route path="site-photos" element={<div>Site Photos - Coming Soon</div>} />
                              <Route path="documents" element={<div>Documents - Coming Soon</div>} />
                              <Route path="geofencing" element={<div>Geofencing - Coming Soon</div>} />
                              <Route path="analytics" element={<AnalyticsDashboard />} />
                              <Route path="" element={<Navigate to="/dashboard" replace />} />
                            </Routes>
                          </RoleProtectedRoute>
                        } 
                      />
                      {/* Public marketplace and information routes */}
                      <Route path="/contractors" element={<ContractorsRouter />} />
                      <Route path="/contractors/:contractorId" element={<EnhancedContractorProfile />} />
                      <Route path="/suppliers" element={<MerchantsPage />} />
                      <Route path="/suppliers/:id" element={<MerchantsPage />} />
                      <Route
                        path="/verifiers"
                        element={
                          <VerifiersPage />
                        }
                      />
                      {/* Property listings vertical — only on deployments with the feature on. */}
                      <Route path="/verify-agent" element={<FeatureGate feature="listings"><VerifyAgentRedirect /></FeatureGate>} />
                      <Route path="/properties" element={<FeatureGate feature="listings"><PropertiesPage /></FeatureGate>} />
                      <Route path="/properties/:id" element={<FeatureGate feature="listings"><PropertyDetailPage /></FeatureGate>} />
                      <Route path="/realtors" element={<FeatureGate feature="listings"><FindRealtorPage /></FeatureGate>} />
                      <Route path="/conveyancers" element={<FeatureGate feature="listings"><FindConveyancerPage /></FeatureGate>} />
                      <Route path="/buying-property" element={<FeatureGate feature="listings"><BuyingPropertyPage /></FeatureGate>} />

                      {/* Merchant-only routes */}
                      <Route
                        path="/merchants/*"
                        element={
                          <RoleProtectedRoute allowedRoles={['merchant']}>
                            <Routes>
                              <Route path="profile" element={<MerchantProfilePage />} />
                              <Route path="profile/create" element={<MerchantProfilePage />} />
                              <Route path="documents" element={<MerchantDocumentsPage />} />
                              <Route path="reviews" element={<MerchantReviewsPage />} />
                              <Route path="quote-requests" element={<MerchantQuoteRequestsPage />} />
                              <Route path="quote-request/:quoteRequestId" element={<MerchantQuoteRequestPage />} />
                              <Route path="orders" element={<MerchantOrdersPage />} />
                              <Route path="products" element={<MerchantProductsPage />} />
                              <Route path="payments" element={<MerchantPaymentsPage />} />
                              <Route path="" element={<Navigate to="/dashboard" replace />} />
                            </Routes>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/merchant/*"
                        element={
                          <RoleProtectedRoute allowedRoles={['merchant']}>
                            {/* Alias tree: keep in parity with /merchants/* so links
                                using either prefix resolve to the same pages. */}
                            <Routes>
                              <Route path="profile" element={<MerchantProfilePage />} />
                              <Route path="profile/create" element={<MerchantProfilePage />} />
                              <Route path="documents" element={<MerchantDocumentsPage />} />
                              <Route path="reviews" element={<MerchantReviewsPage />} />
                              <Route path="quote-requests" element={<MerchantQuoteRequestsPage />} />
                              <Route path="quote-request/:quoteRequestId" element={<MerchantQuoteRequestPage />} />
                              <Route path="orders" element={<MerchantOrdersPage />} />
                              <Route path="products" element={<MerchantProductsPage />} />
                              <Route path="payments" element={<MerchantPaymentsPage />} />
                              <Route path="" element={<Navigate to="/dashboard" replace />} />
                            </Routes>
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Realtor-only routes. With the listings feature off the
                          whole tree collapses to one page that says so — existing
                          realtor accounts must not land in an empty shell. */}
                      <Route
                        path="/realtor/*"
                        element={
                          <RoleProtectedRoute allowedRoles={['realtor']}>
                            <RealtorRoutes />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/conveyancer/*"
                        element={
                          <RoleProtectedRoute allowedRoles={['conveyancer']}>
                            <ConveyancerRoutes />
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Public verifier profile view */}
                      <Route path="/verifiers/:id" element={<VerifierPublicProfile />} />
                      {/* Public profile shortlinks */}
                      <Route path="/p/contractor/:contractorId" element={<EnhancedContractorProfile />} />
                      <Route path="/p/verifier/:id" element={<VerifierPublicProfile />} />
                      {/* Vanity URL for verifier profiles */}
                      <Route path="/v/:slug" element={<VerifierPublicProfile />} />
                      <Route path="/project-managers" element={<ProjectManagersPage />} />
                      <Route path="/project-managers/:id" element={<ProjectManagersPage />} />
                      <Route path="/verification-requests/create" element={<CreateVerificationRequest />} />
                      {/* Services hub — surfaces both service lines for new visitors */}
                      <Route path="/services" element={<ServicesHub />} />
                      {/* Build a Project — public landing (parallel to /property-check) */}
                      <Route path="/build-a-project" element={<BuildAProjectLanding />} />
                      {/* Property Check — public landing + booking wizard */}
                      <Route path="/property-check" element={<PropertyCheckLanding />} />
                      <Route path="/property-check/book" element={<BookPropertyCheck />} />
                      <Route
                        path="/property-check/dashboard"
                        element={
                          <ProtectedRoute>
                            <PropertyCheckDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/property-check/c/:id"
                        element={
                          <ProtectedRoute>
                            <PropertyCheckDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/property-check/payment-success" element={<PropertyCheckPaymentSuccess />} />
                      <Route
                        path="/property-check/properties"
                        element={
                          <ProtectedRoute>
                            <OwnerProperties />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/verification-requests" element={<ClientVerificationRequestsPage />} />
                      <Route path="/verification-requests/:requestId/proposals" element={<VerificationProposalsPage />} />
                      {/* Client-accessible verification report (backend restricts to participants).
                          The same page is also mounted under /verifier/* for verifiers. */}
                      <Route path="/simple-verifications/:id/report" element={<SimpleVerificationReportPage />} />
                      {/* Legacy contractor project browsing path */}
                      <Route 
                        path="/browse-projects" 
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['contractor']}>
                              <Navigate to="/contractor/marketplace" replace />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* General marketplace routes */}
                      <Route path="/marketplace" element={<MarketplacePage />} />
                      <Route path="/marketplace/materials" element={<MaterialsMarketplace />} />
                      <Route path="/marketplace/equipment" element={<EquipmentMarketplace />} />
                      <Route path="/marketplace/professionals" element={<ProfessionalsMarketplace />} />
                      <Route path="/marketplace/insurance" element={<InsuranceMarketplace />} />
                      <Route path="/marketplace/inspectors" element={<InspectorsMarketplace />} />
                      
                      {/* Contractor-specific routes */}
                      <Route 
                        path="/contractor/marketplace" 
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['contractor']}>
                              <ContractorMarketplacePage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/contractor/finances" 
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['contractor']}>
                              <ContractorFinancialHub role="contractor" />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/contractor/documents" 
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['contractor']}>
                              <ContractorDocumentsPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/contractor/history" 
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['contractor']}>
                              <WorkHistoryPage role="contractor" />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* Proposals Management */}
                      <Route 
                        path="/proposals" 
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['contractor']}>
                              <ProposalManagement />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* AI-Powered Bid Management Routes */}
                      <Route 
                        path="/bids" 
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['contractor']}>
                              <ContractorBidsPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/bids/review" 
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['client']}>
                              <ClientBidReviewPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } 
                      />
                      <Route
                        path="/bids/:bidId"
                        element={
                          <ProtectedRoute>
                            <BidDetailView />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/milestone-reports/:id/review"
                        element={
                          <ProtectedRoute>
                            <MilestoneReportReviewPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/milestone-reports/:id"
                        element={
                          <ProtectedRoute>
                            <MilestoneReportReviewPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/projects/:projectId/compare-bids"
                        element={
                          <ProtectedRoute>
                            <RoleProtectedRoute allowedRoles={['client']}>
                              <BidComparisonPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        }
                      />

                      {/* BOQ / Materials Schedule routes */}
                      <Route
                        path="/projects/:projectId/boq"
                        element={
                          <ProtectedRoute>
                            <BOQTrackerPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/projects/:projectId/boq/:boqId"
                        element={
                          <ProtectedRoute>
                            <BOQTrackerPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Messaging routes - Enhanced UI with AI features and project-based routing */}
                      <Route
                        path="/messages"
                        element={
                          <ProtectedRoute>
                            <MessagingPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/messages/:conversationId"
                        element={
                          <ProtectedRoute>
                            <MessagingPage />
                          </ProtectedRoute>
                        }
                      />
                      {/* /messaging is a legacy alias — redirect to canonical /messages */}
                      <Route path="/messaging" element={<Navigate to="/messages" replace />} />
                      <Route path="/messaging/:conversationId" element={<Navigate to="/messages/:conversationId" replace />} />
                      
                      {/* Email verification routes */}
                      <Route path="/verify-email" element={<EmailVerificationFlow />} />
                      <Route 
                        path="/email-verification" 
                        element={
                          <ProtectedRoute>
                            <EmailVerificationFlow />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/notification-preferences" 
                        element={
                          <ProtectedRoute>
                            <NotificationPreferences />
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* Analytics Dashboard */}
                      <Route 
                        path="/analytics" 
                        element={
                          <RoleProtectedRoute allowedRoles={['admin', 'contractor', 'project_manager']}>
                            <AnalyticsDashboard />
                          </RoleProtectedRoute>
                        } 
                      />
                      
                      {/* Supabase Storage Test */}
                      <Route 
                        path="/test-storage" 
                        element={
                          <ProtectedRoute>
                            <SupabaseTestComponent />
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* Logout route */}
                      <Route path="/logout" element={<LogoutHandler />} />
                      
                      {/* Catch-all route */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    </ErrorBoundary>
                  </Layout>
                </Router>
                {/* AI chatbot: no floating button — opens via the dashboard's
                    "Live Chat Support" button (eqaya:open-ai-chat event) */}
                <EqayaAIChatWidget />
              </FeaturesProvider>
            </AuthProvider>
          </ThemeProvider>
        </QueryClientProvider>
    </Provider>
  );
}

export default App;
